import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { brandPropTypes } from "app/utils/propTypes";
import PropTypes from "prop-types";
import "./Listing.scss";
import "src/app/pages/SmartDP/Search/SDPSearchForm/SmartDPSearchForm.scss";
import ListingHeaderContainer from "app/pages/Listing/ListingHeader/ListingHeaderContainer";
import { BRANDS } from "app/constants";
import { AsideProductFiltersContainer } from "app/pages/.shared/AsidePanel/AsideProductFilters/AsideProductFiltersContainer";
import { AsideProductFiltersMenu } from "@brand/AsidePanel/AsideProductFilters/AsideProductFiltersMenu";
import ProductsFilterContainer from "@brand/ProductsFilter/ProductsFilterContainer";
import { scrollToElement } from "app/utils/scroller";
import { forceCheck } from "react-lazyload";
import CookiePolicyFooterContainer from "app/pages/.shared/CookiePolicyFooter/CookiePolicyFooterContainer";
import ListingContentContainer from "app/pages/Listing/ListingContent/ListingContentContainer";
import IconGoToTopArrow from "app/pages/.shared/static/icons/IconGoToTopArrow";
import { PushNotificationContainer } from "app/pages/.shared/PushNotification/PushNotificationContainer";
import PartnerSEO from "app/pages/.shared/PartnerSEO/PartnerSEO";
import ProductsFilter from "@brand/ProductsFilter/ProductsFilter";
import useOnSticky from "app/pages/.shared/useOnSticky";
import classNames from "classnames";
import useIntersectionObserver, {
	INTERSECTION_STATUS,
} from "app/pages/.shared/useIntersectionObserver";

import ListingHeadMetadata from "app/pages/Listing/ListingHeadMetadata";

const Listing = ({ brand, idSpotlight, displayProductsCount, listingSEO = {}, resolution }) => {
	const listingHeaderRef = useRef();
	const filtersRef = useRef();
	const [showScrollTopButton, toggleScrollTopButton] = useState(false);

	const intersectionStatus = useIntersectionObserver(listingHeaderRef.current);

	const isElementOnScreen = useOnSticky({ ref: filtersRef });

	useEffect(() => {
		if (intersectionStatus === INTERSECTION_STATUS.LEAVING_FROM_TOP) {
			toggleScrollTopButton(true);
		} else if (
			intersectionStatus === INTERSECTION_STATUS.LEAVING_FROM_BOTTOM ||
			intersectionStatus === INTERSECTION_STATUS.ENTERING
		) {
			toggleScrollTopButton(false);
		}
	}, [intersectionStatus]);

	const asideProductFiltersMenuComponent = useCallback(
		props => {
			return (
				(brand === BRANDS.AF || brand === BRANDS.TO || brand === BRANDS.UG) && (
					<AsideProductFiltersMenu
						scrollToListingHeader={scrollToListingHeader}
						{...props}
					/>
				)
			);
		},
		[brand]
	);

	useLayoutEffect(() => {
		// force l'affichage des images des produits sur mobile lorsqu'un filtre est actif
		forceCheck();
	}, []);

	const scrollToListingHeader = () => {
		scrollToElement({
			element: listingHeaderRef.current,
			offset: 0,
			disableSmoothScroll: true,
		});
	};

	const handleScrollToTopClick = useCallback(() => {
		window.scrollTo({
			top: 0,
			left: 0,
			behavior: "smooth",
		});
	}, []);

	const scrollTopButtonClass = classNames({
		"scroll-top-button": true,
		"scroll-top-button--visible": showScrollTopButton,
	});

	return (
		<>
			<AsideProductFiltersContainer>
				<ProductsFilterContainer
					asideFilterMenu={false}
					renderComponent={asideProductFiltersMenuComponent}
				/>
			</AsideProductFiltersContainer>

			<div className="listing">
				<ListingHeadMetadata />
				<ListingHeaderContainer
					idSpotlight={idSpotlight}
					scrollToListingHeader={scrollToListingHeader}
					listingHeaderRef={listingHeaderRef}
					displayProductsCount={displayProductsCount}
				/>

				<ProductsFilterContainer
					scrollToListingHeader={scrollToListingHeader}
					renderComponent={props => (
						<ProductsFilter
							{...props}
							className="listing__filters"
							filtersRef={filtersRef}
							isStuck={isElementOnScreen}
							isSticky={true}
						/>
					)}
				/>

				<ListingContentContainer
					displayProductsCount={displayProductsCount}
					spotlightUri={idSpotlight}
				/>

				<PartnerSEO
					title={listingSEO.title}
					description={listingSEO.description}
					resolution={resolution}
				/>

				<span className={scrollTopButtonClass} onClick={handleScrollToTopClick}>
					<IconGoToTopArrow width={50} height={50} />
				</span>

				<CookiePolicyFooterContainer />
			</div>
			<PushNotificationContainer />
		</>
	);
};

Listing.propTypes = {
	brand: brandPropTypes,
	idSpotlight: PropTypes.string,
	listingSEO: PropTypes.object,
	displayProductsCount: PropTypes.bool,
	resolution: PropTypes.string,
	resetAllSDPProductsFilter: PropTypes.func,
};

export default Listing;
